import { useContext, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { offset } from '@floating-ui/dom';
import { type Placement, useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ChatContextType } from '@trinity/chat';
import { ActiveChatSessionContext, ChatSessionFiltersContext } from '@trinity/chat-components/components/Chat/context';
import { useHeaderContext } from '@trinity/chat-components/components/Chat.Header/context';
import { ChatFilterButton } from '@trinity/chat-components/components/FilterButton/index.js';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { calculateActiveFiltersCount } from './common';
import { FilterPaneProfile, FilterPaneWorkspace } from './FilterPane';

type Props = {
  css?: CSSOverrides;
  className?: string;
  placement?: Placement;
  onFiltersConfirmed?: () => void;
};

export function FilterButton({ placement = 'bottom-start', css = {}, className, onFiltersConfirmed }: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const filters = useContext(ChatSessionFiltersContext);
  const filtersAtOpenRef = useRef<typeof filters | null>(null);

  const zIndexMiddleware = useZIndexMiddleware();
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(8), zIndexMiddleware],
    placement,
  });

  const setReferenceRef = useCallback((node: HTMLButtonElement | null) => {
    refs.setReference(node);
  }, [refs]);

  const setFloatingRef = useCallback((node: HTMLDivElement | null) => {
    refs.setFloating(node);
  }, [refs]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!open) {
      filtersAtOpenRef.current = filters;
    } else {
      if (filtersAtOpenRef.current !== null && filtersAtOpenRef.current !== filters) {
        onFiltersConfirmed?.();
      }
      filtersAtOpenRef.current = null;
    }
    setOpen(prev => !prev);
    e.stopPropagation();
  }, [open, filters, onFiltersConfirmed]);

  const handleClickAway = useCallback(() => {
    if (filtersAtOpenRef.current !== null && filtersAtOpenRef.current !== filters) {
      onFiltersConfirmed?.();
    }
    filtersAtOpenRef.current = null;
    setOpen(false);
  }, [filters, onFiltersConfirmed]);

  const ctx = useHeaderContext();
  const activeFiltersCount = useActiveFiltersCount();

  const display = {
    activeFilters: activeFiltersCount > 0,
  };

  return (
    <Root className={className}>
      <ChatFilterButton
        css={css}
        ref={setReferenceRef}
        variant="chart"
        onClick={handleClick}
        count={activeFiltersCount}
        aria-label="Filter" />
      {open && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            {createPortal(
              <Popper
                ref={setFloatingRef}
                style={floatingStyles} >
                {ctx.displayContextType === ChatContextType.KolProfile
                  ? <FilterPaneProfile active={display.activeFilters} />
                  : <FilterPaneWorkspace active={display.activeFilters} />}
              </Popper>,
              document.body,
            )}
          </div>
        </ClickAwayListener>
      )}
    </Root>
  );
}

function useActiveFiltersCount() {
  const filters = useContext(ChatSessionFiltersContext);
  const { context } = useContext(ActiveChatSessionContext);
  const features = useContext(GroupFeaturesContext);

  const resolvedFeatures = useMemo(() => {
    return {
      hcpProfiles: features?.hcpProfiles ?? false,
      workspaces: features?.workspaces ?? false,
      engagementFilter: features?.engagementFilter ?? false,
    };
  }, [features?.engagementFilter, features?.hcpProfiles, features?.workspaces]);

  return useMemo(() => calculateActiveFiltersCount({
    context,
    data: filters,
    features: resolvedFeatures,
  }), [context, resolvedFeatures, filters]);
}

const Root = styled.div({});

const Popper = styled.div({});
